import { Service } from '@toeverything/infra';

import type { DocsService } from '../../doc';
import type { I18nService } from '../../i18n';
import { TemplateDocList } from '../entities/list';
import { TemplateDocSetting } from '../entities/setting';
import { ensureAllTemplates } from '../templates';
import {
  resolveTemplateLang,
  setTemplateLang,
} from '../templates/team-templates';

/**
 * Service for managing document templates in a workspace.
 * Automatically creates built-in templates when initialized.
 *
 * Template language is derived from the user's AFFiNE language preference
 * (Settings > Appearance > Language), defaulting to Spanish.
 */
export class TemplateDocService extends Service {
  public readonly list = this.framework.createEntity(TemplateDocList);
  public readonly setting = this.framework.createEntity(TemplateDocSetting);

  private initialized = false;

  constructor(
    private readonly docsService: DocsService,
    private readonly i18nService: I18nService
  ) {
    super();
    // Initialize templates when service is created
    this.initializeTemplates();
  }

  private initializeTemplates() {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    // Delay to ensure docs service is ready
    setTimeout(() => {
      this.ensureBuiltInTemplates().catch(error => {
        console.error('Failed to create built-in templates:', error);
      });
    }, 1000);
  }

  /**
   * Ensures all built-in templates exist in the workspace.
   *
   * Templates include:
   * - Sprint Planning, 1:1 Meeting, Weekly Standup
   * - Project Tracker, Retrospective
   * - Bug Report, Feature Request
   * - Team Wiki, Decision Log
   *
   * Note: Meeting-related templates (except 1:1 Meeting) are filtered
   * from the template selector since the Meeting Notes database is
   * created automatically via the sidebar Meetings feature.
   */
  public async ensureBuiltInTemplates(): Promise<void> {
    // Set template language from user's AFFiNE preference before creating
    const langKey = this.i18nService.i18n.currentLanguageKey$.value;
    setTemplateLang(resolveTemplateLang(langKey));

    const docsService = this.docsService;
    // Create all team templates (meetings templates are filtered from selector)
    await ensureAllTemplates(docsService, this.list);
  }
}
